import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../../styled/Icon';
import ImageBox from '../../styled/ImageBox';
import { Image, Picture } from '../../styled/Image';
import clsx from 'clsx';
import VideoEmbed from '../../styled/VideoEmbed';

import {
  ControlLeft,
  ControlRight,
  Slider,
  SliderContainer,
  SliderControls,
  SliderItem,
} from '../../styled/Slider/wrappers';
import Skeleton from '../../styled/Skeleton';

const getVisibleChildren = (element: HTMLElement | null) => {
  if (!element?.children) {
    return [];
  }

  return Array.from(element.children)?.filter((e) => (e as HTMLElement).offsetParent !== null);
};

interface ISliderItem {
  className?: string;
}

interface ISliderImage extends ISliderItem {
  src: string;
  srcSet: string;
  sizes: string;
  alt?: string;
}

interface ISliderSource {
  srcSet: string;
  sizes?: string;
  media?: string;
  type?: string;
}
interface ISliderSources extends ISliderItem {
  sources?: ISliderSource[];
  fallbackImage: ISliderImage;
}

interface ISliderVideo extends ISliderItem {
  youtubeId: string;
}

interface IImageSlider {
  id?: string;
  images?: ISliderImage[];
  multiFormatImages?: ISliderSources[];
  index?: number;
  inPanel?: boolean;
  onClick?: () => void;
  onIndexChange: (index: number) => void;
  showControls?: boolean;
  videos?: ISliderVideo[];
}

const ImageSlider = ({
  id,
  onClick,
  onIndexChange,
  index = 0,
  inPanel,
  images = [],
  multiFormatImages = [],
  showControls,
  videos,
}: IImageSlider) => {
  const slider = useRef<HTMLDivElement | null>(null);
  const timeout = useRef<any>(null);
  const [load, setLoad] = useState([0]);

  useEffect(() => {
    slider.current?.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const sliderWidth = slider.current?.clientWidth || 0;
    slider.current?.scrollTo({
      top: 0,
      left: sliderWidth * index,
      behavior: 'smooth',
    });
  }, [index]);

  useEffect(() => {
    const scrollHandler = () => {
      const sliderWidth = slider.current?.clientWidth || 0;
      const nextIndex = Math.round((slider?.current?.scrollLeft || 0) / sliderWidth);
      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => onIndexChange(nextIndex), 50);

      if (load.indexOf(nextIndex) === -1) {
        setLoad((prev) => prev.concat([nextIndex]));
      }
    };

    if (slider.current) {
      slider.current.addEventListener('scroll', scrollHandler);
    }

    return () => slider.current?.removeEventListener('scroll', scrollHandler);
  }, []);

  const back = useCallback(() => {
    const itemCount = getVisibleChildren(slider.current).length;
    const nextIndex = index - 1 < 0 ? itemCount - 1 : index - 1;

    onIndexChange(nextIndex);

    if (load.indexOf(nextIndex) === -1) {
      setLoad((prev) => prev.concat([nextIndex]));
    }
  }, [index]);

  const forward = useCallback(() => {
    const itemCount = getVisibleChildren(slider.current).length;
    const nextIndex = index + 1 >= itemCount ? 0 : index + 1;

    onIndexChange(nextIndex);

    if (load.indexOf(nextIndex) === -1) {
      setLoad((prev) => prev.concat([nextIndex]));
    }
  }, [index]);

  if (images.length < 1 && multiFormatImages.length < 1 && videos && videos.length < 1) {
    return <Skeleton desktopRatio="851:597" mobileRatio="851:597" />;
  }

  return (
    <SliderContainer className={clsx(inPanel && 'in-panel')}>
      <Slider ref={slider}>
        {images.map((item, i) => (
          <SliderItem key={`${item.src}-${i}`} onClick={onClick} className={item.className}>
            <ImageBox ratio={533 / 760}>
              {load.indexOf(i) >= 0 && (
                <Image src={item.src} srcSet={item.srcSet} sizes={item.sizes} alt={item.alt} />
              )}
            </ImageBox>
          </SliderItem>
        ))}
        {multiFormatImages.map((item, i) => (
          <SliderItem
            key={`${item.fallbackImage?.src}-${i}`}
            onClick={onClick}
            className={item.className}>
            <ImageBox ratio={533 / 760}>
              {load.indexOf(i) >= 0 && (
                <Picture sources={item.sources} image={item.fallbackImage} />
              )}
            </ImageBox>
          </SliderItem>
        ))}
        {videos?.map((item) => (
          <SliderItem key={item.youtubeId} onClick={onClick} className={item.className}>
            <VideoEmbed id={item.youtubeId} />
          </SliderItem>
        ))}
      </Slider>
      {showControls && (
        <SliderControls>
          <ControlLeft onClick={back}>
            <ChevronLeftIcon strokeWidth="2" />
          </ControlLeft>
          <ControlRight onClick={forward}>
            <ChevronRightIcon strokeWidth="2" />
          </ControlRight>
        </SliderControls>
      )}
    </SliderContainer>
  );
};

export default ImageSlider;
